/**
 * Persistence Slice
 *
 * Manages URL state synchronization and restoration.
 */

import { urlState } from '@/services/url-state';

export interface PersistenceSlice {
  // Actions
  restoreFromUrl: () => void;
  syncToUrl: () => void;
}

export const createPersistenceSlice = (set: any, get: any): PersistenceSlice => ({
  /**
   * Restore state from URL query parameters
   *
   * Called on page load after location data is available.
   * Validates that referenced IDs exist in the loaded data.
   * If state already exists (e.g., from a previous restore), skip restoration.
   */
  restoreFromUrl: () => {
    const urlParams = urlState.getState();
    // Access cross-slice data via get()
    const state = get() as any;
    const { selectedLocationId } = state;
    const allLocations = state.getAllLocations();

    // Skip if we already have a selection (avoid overwriting user interaction)
    if (selectedLocationId !== null) {
      return;
    }

    // Restore location selection
    if (urlParams.loc !== undefined) {
      const locationExists = allLocations.some((loc: any) => loc.id === urlParams.loc);
      if (locationExists) {
        set({ selectedLocationId: urlParams.loc });
      } else {
        console.warn('[LocationStore] Location ID not found:', urlParams.loc);
      }
    }

    // Restore date selection
    if (urlParams.date) {
      set({ selectedDate: urlParams.date });
    }

    // Restore stop/timeslot selection (validate against selected location)
    if (urlParams.slot !== undefined && urlParams.loc !== undefined) {
      const location = allLocations.find((loc: any) => loc.id === urlParams.loc);
      if (location) {
        const stopExists = location.stops.some((stop: any) => stop.id === urlParams.slot);
        if (stopExists) {
          set({ selectedStopId: urlParams.slot });
        } else {
          console.warn('[LocationStore] Stop ID not found:', urlParams.slot, 'in location:', urlParams.loc);
        }
      }
    }

    // Restore search query from URL
    // Note: Don't use updateFilters() here because it would trigger syncToUrl()
    // which would be circular. Just set the state directly.
    if (urlParams.search) {
      set((state: any) => ({
        filters: {
          ...state.filters,
          searchQuery: urlParams.search as string,
        },
      }));
    }
  },

  /**
   * Sync current state to URL
   *
   * Updates URL with current selection state.
   * Use pushState option for major navigation (location selection).
   */
  syncToUrl: () => {
    // Access cross-slice data via get()
    const state = get() as any;
    const { selectedLocationId, selectedDate, selectedStopId, filters } = state;

    // Always sync search query to URL (don't require searchPoint)
    // Empty string means clear the search param
    const searchToSync = filters.searchQuery || undefined;

    // Build URL state from current store state
    urlState.updateState(
      {
        loc: selectedLocationId ?? undefined,
        date: selectedDate ?? undefined,
        slot: selectedStopId ?? undefined,
        search: searchToSync,
      },
      {
        // Use pushState for location changes (major navigation)
        // Use replaceState for date/slot changes (refinement)
        pushState: false,
      }
    );
  },
});
